import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./select-dropdown-field.module.scss";
import { ExpandIcon, CheckIcon, CloseIcon } from "../../../../assets/icons";
import useWindowDimensions from "../../../../hooks/use-window-dimensions";

export interface DropdownOption {
	value: string;
	label: string;
}

interface SelectDropdownFieldProps {
	options: DropdownOption[];
	placeholder?: string;
	multiple?: boolean;
	selectedValue?: string;
	selectedValues?: string[];
	onSelectSingle?: (option: DropdownOption) => void;
	onSelectMulti?: (options: DropdownOption[]) => void;
	hasError?: boolean;
	disabled?: boolean;
}

const MENU_GAP = 6; // px between trigger and menu
const MENU_MAX_HEIGHT = 240; // 15rem cap

const SelectDropdownField: React.FC<SelectDropdownFieldProps> = ({
	options,
	placeholder = "Select...",
	multiple = false,
	selectedValue,
	selectedValues = [],
	onSelectSingle,
	onSelectMulti,
	hasError = false,
	disabled = false,
}) => {
	const [open, setOpen] = useState(false);
	const { isMobile } = useWindowDimensions();
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLUListElement>(null);
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

	// Position the desktop popover relative to the trigger, flipping up when short on space below.
	const updatePosition = useCallback(() => {
		const el = triggerRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const openUp = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;
		if (openUp) {
			setMenuStyle({
				position: "fixed",
				left: rect.left,
				width: rect.width,
				bottom: window.innerHeight - rect.top + MENU_GAP,
				maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(0, rect.top - MENU_GAP - 8)),
			});
		} else {
			setMenuStyle({
				position: "fixed",
				left: rect.left,
				width: rect.width,
				top: rect.bottom + MENU_GAP,
				maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(0, spaceBelow - MENU_GAP - 8)),
			});
		}
	}, []);

	useLayoutEffect(() => {
		if (open && !isMobile) updatePosition();
	}, [open, isMobile, updatePosition]);

	// Desktop: keep the popover anchored while scrolling/resizing.
	useEffect(() => {
		if (!open || isMobile) return;
		const handle = () => updatePosition();
		window.addEventListener("scroll", handle, true);
		window.addEventListener("resize", handle);
		return () => {
			window.removeEventListener("scroll", handle, true);
			window.removeEventListener("resize", handle);
		};
	}, [open, isMobile, updatePosition]);

	// Desktop: close when clicking outside both the trigger and the (portaled) menu.
	useEffect(() => {
		if (!open || isMobile) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (menuRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open, isMobile]);

	// Mobile: lock background scroll while the bottom sheet is open.
	useEffect(() => {
		if (!open || !isMobile) return;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, [open, isMobile]);

	const isSelected = (value: string) => (multiple ? selectedValues.includes(value) : selectedValue === value);

	const handleOptionClick = (option: DropdownOption) => {
		if (multiple) {
			const next = selectedValues.includes(option.value)
				? options.filter(o => selectedValues.includes(o.value) && o.value !== option.value)
				: options.filter(o => selectedValues.includes(o.value) || o.value === option.value);
			onSelectMulti?.(next);
		} else {
			onSelectSingle?.(option);
			setOpen(false);
		}
	};

	const displayText = (): string => {
		if (multiple) {
			if (selectedValues.length === 0) return placeholder;
			const first = options.find(o => o.value === selectedValues[0])?.label ?? "";
			return selectedValues.length > 1 ? `${first} +${selectedValues.length - 1}` : first;
		}
		return options.find(o => o.value === selectedValue)?.label ?? placeholder;
	};

	const hasValue = multiple ? selectedValues.length > 0 : !!selectedValue;

	const popover = createPortal(
		<ul className={styles.menu} role="listbox" ref={menuRef} style={menuStyle}>
			{options.map(option => (
				<li key={option.value} role="option" aria-selected={isSelected(option.value)}>
					<button type="button" className={styles.option} onClick={() => handleOptionClick(option)}>
						<span>{option.label}</span>
						{isSelected(option.value) && <CheckIcon className={styles.check} />}
					</button>
				</li>
			))}
		</ul>,
		document.body
	);

	const bottomSheet = createPortal(
		<div className={styles.sheetOverlay} onMouseDown={() => setOpen(false)}>
			<div className={styles.sheet} role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()}>
				<div className={styles.sheetHeader}>
					<button type="button" className={styles.sheetClose} onClick={() => setOpen(false)} aria-label="Close">
						<CloseIcon />
					</button>
					<span className={styles.sheetTitle}>{placeholder}</span>
				</div>
				<div className={styles.sheetDivider} />
				<ul className={styles.sheetList} role="listbox">
					{options.length === 0 ? (
						<li className={styles.sheetEmpty}>No items available</li>
					) : (
						options.map(option => (
							<li key={option.value} role="option" aria-selected={isSelected(option.value)}>
								<button type="button" className={styles.sheetOption} onClick={() => handleOptionClick(option)}>
									<span>{option.label}</span>
									{isSelected(option.value) && <CheckIcon className={styles.check} />}
								</button>
							</li>
						))
					)}
				</ul>
			</div>
		</div>,
		document.body
	);

	return (
		<div className={styles.container} ref={containerRef}>
			<button
				type="button"
				ref={triggerRef}
				disabled={disabled}
				className={`${styles.trigger} ${hasError ? styles.error : ""}`}
				onClick={() => !disabled && setOpen(o => !o)}
			>
				<span className={hasValue ? styles.value : styles.placeholder}>{displayText()}</span>
				<ExpandIcon className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} />
			</button>

			{open && (isMobile ? bottomSheet : popover)}
		</div>
	);
};

export default SelectDropdownField;
